import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, AlertCircle, Info, X, ChevronRight, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { useAuth, API } from "../App";
import axios from "axios";

const AlertsDropdown = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  useEffect(() => {
    if (token) {
      fetchAlerts();
    }
  }, [token]);

  // Poll for alerts every 60 seconds
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/alerts`, { headers });
      
      // Filter out dismissed alerts
      const activeAlerts = response.data.alerts.filter(
        alert => !dismissedAlerts.has(alert.alert_id)
      );
      
      setAlerts(activeAlerts);
      setUnreadCount(activeAlerts.length);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertClick = (alert) => {
    setOpen(false);
    if (alert.link) {
      navigate(alert.link);
    }
  };

  const handleDismiss = async (e, alertId) => {
    e.stopPropagation();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/alerts/${alertId}/dismiss`, {}, { headers });
      
      setDismissedAlerts(prev => new Set([...prev, alertId]));
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to dismiss alert:", error);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case "info":
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getAlertBgColor = (type) => {
    switch (type) {
      case "error":
        return "bg-red-50 border-red-100 hover:bg-red-100";
      case "warning":
        return "bg-amber-50 border-amber-100 hover:bg-amber-100";
      case "info":
      default:
        return "bg-blue-50 border-blue-100 hover:bg-blue-100";
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: "bg-red-100 text-red-700",
      medium: "bg-amber-100 text-amber-700",
      low: "bg-slate-100 text-slate-600"
    };
    return colors[priority] || colors.low;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-2xl hover:bg-slate-100 transition-colors"
          data-testid="alerts-trigger"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent 
        align="end" 
        className="w-96 p-0 rounded-2xl shadow-xl border-slate-200"
        data-testid="alerts-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Alerts</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>

        {/* Alerts List */}
        <div className="max-h-96 overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="w-12 h-12 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-3">
                <Bell className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500">No alerts at this time</p>
              <p className="text-xs text-slate-400 mt-1">You&apos;re all caught up!</p>
            </div>
          ) : (
            <div className="py-2">
              {alerts.map((alert) => (
                <div
                  key={alert.alert_id}
                  onClick={() => handleAlertClick(alert)}
                  className={`mx-2 mb-2 p-3 rounded-xl border cursor-pointer transition-all ${getAlertBgColor(alert.type)}`}
                  data-testid={`alert-item-${alert.alert_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {getAlertIcon(alert.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-slate-900">
                          {alert.title}
                        </span>
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${getPriorityBadge(alert.priority)}`}>
                          {alert.priority?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 line-clamp-2">
                        {alert.message}
                      </p>
                      {alert.link && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                          <span>View details</span>
                          <ChevronRight className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => handleDismiss(e, alert.alert_id)}
                      className="p-1 rounded-lg hover:bg-white/50 transition-colors"
                      data-testid={`dismiss-alert-${alert.alert_id}`}
                    >
                      <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {alerts.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-slate-600 hover:text-slate-900 rounded-xl"
              onClick={() => {
                setAlerts([]);
                setUnreadCount(0);
              }}
              data-testid="clear-all-alerts"
            >
              Clear All
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default AlertsDropdown;
